import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import UploaderWizard from 'Components/UploaderWizard';
import { selectMerchantOverridesBulkUploadConfig } from 'Selectors';
import { useHistory } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { postMerchantOverrides } from 'Services';
import { useQueryClient } from '@tanstack/react-query';
import appStyles from 'Routes/Routes.css';
import styles from './index.css';

export default function BulkUploadPage() {
    const { t } = useTranslation();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const queryClient = useQueryClient();
    const merchantOverridesUploadConfig = useSelector(selectMerchantOverridesBulkUploadConfig);
    if (!merchantOverridesUploadConfig) return null;

    const { maxFileSize, fileTypeOptions, columns } = merchantOverridesUploadConfig;
    const processedUploaderConfig = {
        maxSize: maxFileSize,
        fileTypes: fileTypeOptions,
        columns: columns.map(({ required, key, title, acceptableHeaders }: UploaderColumn) => ({
            required,
            accessor: key,
            Header: t(title),
            acceptableHeaders
        }))
    };

    const postOverrides = async (parsedFileData: any, matchedColumnsMap: Record<string, any>) => {
        const Payload = parsedFileData.map((item: any) => ({
            merchantId: item[matchedColumnsMap.identifier],
            ruleId: item[matchedColumnsMap.ruleId],
            score: item[matchedColumnsMap.score],
            conclusion: item[matchedColumnsMap.conclusion],
            active: item[matchedColumnsMap.active],
            threshold: item[matchedColumnsMap.threshold]
        }));

        return postMerchantOverrides(Payload).then(async ({ data }) => {
            if (data) {
                history.push('/overrides');
                const totalNumOverrides = data?.totalNumOfOverrides;
                const numUpdatedOverrides = data?.numOfUpdatedOverrides;
                const numNewOverrides = data?.numOfNewOverrides;
                const content = (
                    <>
                        <div className={styles.successContent}>
                            <span className={styles.bolded}>
                                {t('preview.overrides.count', { count: totalNumOverrides })}{' '}
                            </span>
                            {t('were successfully uploaded')}.{' '}
                            <span className={styles.bolded}>
                                {t('numSuccessUpdatedMerchants', { count: numUpdatedOverrides })}
                            </span>{' '}
                            {t('and')}{' '}
                            <span className={styles.bolded}>
                                {t('numSuccessNewMerchants', { count: numNewOverrides })}
                            </span>{' '}
                            {t('numSuccessNewMerchantsPt2', { count: numNewOverrides })}
                        </div>
                    </>
                );
                await queryClient.invalidateQueries();
                addModal({
                    id: 'merchant-overrides-upload-success',
                    status: 'success',
                    title: 'Bulk Upload Success',
                    cancelText: t('Close'),
                    content
                });
            }
        });
    };

    const errorContentRenderer = (result: UploadErrorsType) => {
        return (
            <div className={styles.errorBodyMsg}>
                <div className={styles.errPartiallySuccessfulOverridesMsg} data-testid='overrides-partial-success-msg'>
                    {t('uploadOverrideUpdatedPartialSuccess', { count: result?.numOfUpdatedOverrides })}{' '}
                    {t('uploadOverrideCreatedPartialSuccess', { count: result?.numOfNewOverrides })}
                </div>
                <span className={styles.errUnsuccessfulRulesMsg}>
                    {t('uploadOverrideErrors', { count: result?.errors?.length })}
                </span>{' '}
                {t('uploadOverrideErrorsPt2', { count: result?.errors?.length })} {t('uploadOverrideErrorsPt3')}
            </div>
        );
    };

    const previewContentRenderer = (parsedFileTableData: ParsedFileTableData) => {
        return (
            <>
                {t('Your file contains')}
                <span className={styles.rulesNumber}>
                    {t('preview.overrides.count', { count: parsedFileTableData?.data?.length })}
                </span>
                {t('for')}
                <span className={styles.rulesNumber}>
                    {t('preview.merchants.count', {
                        count: new Set(parsedFileTableData?.data?.map((i: any) => i.identifier)).size
                    })}
                    .
                </span>
            </>
        );
    };

    return (
        <div className={appStyles.landingPage}>
            <UploaderWizard
                uploaderConfig={processedUploaderConfig}
                rulesType='merchantOverrides'
                rulesTypeText={t('Merchant Overrides')}
                specificUploadInstructions={t('upload merchant override instructions')}
                previewTitle={t('preview merchant override title')}
                postFn={postOverrides}
                onCancel={() => history.push('/overrides')}
                errorContentRenderer={errorContentRenderer}
                previewContentRenderer={previewContentRenderer}
            />
        </div>
    );
}
